"use client";

import { Checkbox } from "@/components/ui/checkbox";
import type { PublicMenu } from "./domain";
import {
  describePublicSectionRule,
  type PublicMenuSelectionDraft,
  validatePublicMenuSelections,
} from "./menu-selection-state";

type MenuSelectionEditorProps = {
  menu: PublicMenu;
  guestCount: number;
  value: PublicMenuSelectionDraft[];
  onChange: (value: PublicMenuSelectionDraft[]) => void;
  title?: string;
  description?: string;
  guestNoun?: string;
};

export function MenuSelectionEditor({
  menu,
  guestCount,
  value,
  onChange,
  title = "Pre-visit menu",
  description = "Complete required choices for each guest. The restaurant receives these selections with the reservation.",
  guestNoun = "Guest",
}: MenuSelectionEditorProps) {
  const valid = validatePublicMenuSelections(menu, value, guestCount);

  const toggleItem = (
    guestNumber: number,
    item: PublicMenu["sections"][number]["items"][number],
    checked: boolean,
  ) => {
    if (checked) {
      onChange([
        ...value,
        {
          guestNumber,
          itemId: item.id,
          modifiers: (item.modifierGroups ?? []).map((group) => ({
            groupId: group.id,
            optionIds: [],
          })),
        },
      ]);
      return;
    }
    onChange(
      value.filter(
        (selection) =>
          selection.guestNumber !== guestNumber ||
          selection.itemId !== item.id,
      ),
    );
  };

  const toggleModifier = (
    guestNumber: number,
    itemId: string,
    groupId: string,
    optionId: string,
    checked: boolean,
  ) => {
    onChange(
      value.map((selection) => {
        if (
          selection.guestNumber !== guestNumber ||
          selection.itemId !== itemId
        )
          return selection;
        return {
          ...selection,
          modifiers: selection.modifiers.map((group) =>
            group.groupId === groupId
              ? {
                  ...group,
                  optionIds: checked
                    ? [...group.optionIds, optionId]
                    : group.optionIds.filter((id) => id !== optionId),
                }
              : group,
          ),
        };
      }),
    );
  };

  return (
    <section className="event-menu-selections reservation-menu-selections">
      <header>
        <span className="step-label">{title}</span>
        <h3>{menu.name}</h3>
        <p>{description}</p>
      </header>
      {Array.from({ length: guestCount }, (_, index) => index + 1).map(
        (guestNumber) => (
          <div className="event-menu-ticket" key={guestNumber}>
            <strong>
              {guestNoun} {guestNumber}
            </strong>
            {menu.sections.map((section) => {
              const sectionItemIds = new Set(
                section.items.map((item) => item.id),
              );
              const selectedForGuest = value.filter(
                (selection) =>
                  selection.guestNumber === guestNumber &&
                  sectionItemIds.has(selection.itemId),
              );
              const selectedAcrossReservation = value.filter((selection) =>
                sectionItemIds.has(selection.itemId),
              );
              const limitType = section.limitType ?? "unlimited";
              const orderMaximum =
                limitType === "one_per_order"
                  ? 1
                  : limitType === "one_per_person"
                    ? guestCount
                    : limitType === "custom"
                      ? (section.maximumSelections ?? 0)
                      : Number.POSITIVE_INFINITY;
              return (
                <div className="event-menu-section-choice" key={section.id}>
                  <fieldset className="event-menu-item-options">
                    <legend>
                      {section.name}{" "}
                      <small>
                        {describePublicSectionRule(section, guestCount)}
                      </small>
                    </legend>
                    {section.description ? <p>{section.description}</p> : null}
                    {section.items.map((item) => {
                      const checked = selectedForGuest.some(
                        (selection) => selection.itemId === item.id,
                      );
                      const sameItemConflict =
                        section.sameItemForAllGuests === true &&
                        selectedAcrossReservation.length > 0 &&
                        !selectedAcrossReservation.some(
                          (selection) => selection.itemId === item.id,
                        );
                      const disabled =
                        item.orderable === false ||
                        (!checked &&
                          (selectedAcrossReservation.length >= orderMaximum ||
                            (limitType === "one_per_person" &&
                              selectedForGuest.length >= 1) ||
                            sameItemConflict));
                      return (
                        <label key={item.id}>
                          <Checkbox
                            checked={checked}
                            disabled={disabled}
                            onCheckedChange={(next) =>
                              toggleItem(
                                guestNumber,
                                item,
                                next === true,
                              )
                            }
                          />
                          <span>
                            <strong>{item.name}</strong>
                            {item.description ? (
                              <small>{item.description}</small>
                            ) : null}
                            {item.availabilityLabel ? (
                              <small
                                className={
                                  item.orderable === false
                                    ? "menu-stock-unavailable"
                                    : "menu-stock-remaining"
                                }
                              >
                                {item.availabilityLabel}
                              </small>
                            ) : null}
                          </span>
                        </label>
                      );
                    })}
                  </fieldset>
                  {selectedForGuest.flatMap((selection) => {
                    const item = section.items.find(
                      (candidate) => candidate.id === selection.itemId,
                    );
                    if (!item) return [];
                    return (item.modifierGroups ?? []).map((group) => {
                      const selectedOptions =
                        selection.modifiers.find(
                          (candidate) => candidate.groupId === group.id,
                        )?.optionIds ?? [];
                      return (
                        <fieldset key={`${selection.itemId}:${group.id}`}>
                          <legend>
                            {item.name} · {group.name}{" "}
                            <small>
                              {group.minSelections}–{group.maxSelections}
                              {group.sameOptionForAllGuests
                                ? " · same choice for every guest"
                                : ""}
                            </small>
                          </legend>
                          {group.options.map((option) => (
                            <label key={option.id}>
                              <Checkbox
                                checked={selectedOptions.includes(option.id)}
                                disabled={
                                  option.orderable === false ||
                                  (!selectedOptions.includes(option.id) &&
                                    selectedOptions.length >=
                                      group.maxSelections)
                                }
                                onCheckedChange={(next) =>
                                  toggleModifier(
                                    guestNumber,
                                    item.id,
                                    group.id,
                                    option.id,
                                    next === true,
                                  )
                                }
                              />
                              <span>
                                {option.name}
                                {option.availabilityLabel ? (
                                  <small
                                    className={
                                      option.orderable === false
                                        ? "menu-stock-unavailable"
                                        : "menu-stock-remaining"
                                    }
                                  >
                                    {option.availabilityLabel}
                                  </small>
                                ) : null}
                              </span>
                            </label>
                          ))}
                        </fieldset>
                      );
                    });
                  })}
                </div>
              );
            })}
          </div>
        ),
      )}
      {!valid ? (
        <p className="booking-error">
          Complete required menu choices and use the same marked options for
          every guest.
        </p>
      ) : null}
    </section>
  );
}
